import { useCallback, useMemo } from 'react';
import { Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useFocusManagement } from '../../../../hooks/useFocusManagement';
import { useDrawingTools } from '../../../../hooks/useDrawingTools';
import {
  useSectionLayoutMode,
  useSlidePanelVariant,
  useWorkspaceMemberSuggestions,
} from '@/hooks';
import { useGeoMarkupStore } from '../../../../store';
import { selectSelectedTask } from '@/store/selectors/taskSelectors';
import { useTaskActions } from '../../../../hooks/useTaskActions';
import {
  useWizardSectionNav,
  validateRequiredFields,
  WizardStepRenderer,
} from '@/components/common/wizard';
import { DetailPanelLayout } from '@/components/common/DetailPanelLayout';
import { FormFooter, FormPanelTitle } from '@/components/common/form';
import {
  buildTaskWizardStepDescriptors,
  type TaskWizardStepDescriptor,
} from '../config/wizardStepDescriptors';
import type { TaskGeometry } from '../../../../types';
import { SlidePanel } from '../../../ui/SlidePanel';
import { useTaskWizardLogic } from '../hooks/useTaskWizardLogic';
import styles from '@/components/common/wizard/wizardContent.module.css';

export function TaskFormWizard() {
  const isOpen = useGeoMarkupStore((state) => state.tasks.formIsOpen);
  // Tab visibility derives from data already loaded — not a freshly-triggered
  // LIST fetch. `mapTasks` (all tasks, unfiltered) is fetched on map startup
  // alongside notes/obstructions, so the Related Tasks tab shows correctly even
  // after a refresh on the notes view. `items` covers the /tasks route (no map).
  const mapTasksCount = useGeoMarkupStore(
    (state) => state.tasks.mapTasks.length
  );
  const listTasksCount = useGeoMarkupStore((state) => state.tasks.items.length);
  const allTasksCount = Math.max(mapTasksCount, listTasksCount);
  const taskFormMode = useGeoMarkupStore((state) => state.tasks.formMode);
  const selectedTaskId = useGeoMarkupStore((state) => state.tasks.selectedId);
  const selectedTask = useGeoMarkupStore(selectSelectedTask);

  const editingTask = useMemo(
    () => (taskFormMode === 'edit' ? selectedTask : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskFormMode, selectedTaskId]
  );
  const isGeometriesListOpen = useGeoMarkupStore(
    (state) => state.ui.geometriesList.isOpen
  );
  const isRelatedTasksListOpen = useGeoMarkupStore(
    (state) => state.ui.relatedTasksList.isOpen
  );
  const setMobilePanelMinimized = useGeoMarkupStore(
    (state) => state.ui.setMobilePanelMinimized
  );
  const { t } = useTranslation();
  const slidePanelVariant = useSlidePanelVariant();
  // Desktop fullscreen renders the section sidebar; geometry then can't be drawn
  // from a map-covering panel, so it stops gating submit (mirrors prior logic).
  const { isSidebar } = useSectionLayoutMode();

  const { currentGeometries } = useDrawingTools();

  const availableTasksCount = useMemo(() => {
    return editingTask ? allTasksCount - 1 : allTasksCount;
  }, [allTasksCount, editingTask]);

  // Single source of truth for the wizard sections: Related Tasks is dropped
  // when there are no other tasks to link to.
  const stepDescriptors = useMemo<TaskWizardStepDescriptor[]>(
    () => buildTaskWizardStepDescriptors(availableTasksCount),
    [availableTasksCount]
  );

  const {
    form,
    isSubmitting,
    handleClose,
    handleSubmit,
    handleStepClick,
    handleManualRoadInput,
    handleRequestGPSDetection,
    gpsLocationSuggestion,
    roadSource,
    isWaitingForInitialGPS,
  } = useTaskWizardLogic({ editingTask, steps: stepDescriptors });

  const { deleteAttachment } = useTaskActions();

  const { members, isLoading: isMembersLoading } =
    useWorkspaceMemberSuggestions({ enabled: isOpen });

  const handleDeleteExistingAttachment = useCallback(
    (attachmentId: string) => {
      if (!editingTask) return;
      deleteAttachment(editingTask.id, attachmentId);
    },
    [editingTask, deleteAttachment]
  );

  useFocusManagement({
    isOpen,
    containerRef: { current: null }, // Will be handled by Mantine Modal
    onEscape: () => handleClose(),
    autoFocus: true,
  });

  // Per-section validity (required fields filled + error-free). Computed once and
  // shared by the section tabs (attention dot) and submit gating.
  const sectionValidity = useMemo(
    () =>
      stepDescriptors.map(
        (descriptor) =>
          validateRequiredFields(
            descriptor.requiredFields,
            form.values,
            form.errors
          ).isValid
      ),
    [stepDescriptors, form.values, form.errors]
  );

  // Bridge the numeric section pointer (`currentStep`) to DetailPanelLayout's
  // string-keyed section API; free navigation + submit-time validation.
  const { activeSection, handleSectionChange, sectionItems } =
    useWizardSectionNav({
      stepDescriptors,
      currentStep: form.values.currentStep,
      sectionValidity,
      onStepSelect: handleStepClick,
    });

  const canSave = useMemo(() => {
    const step1Valid = sectionValidity[0] ?? false;
    const hasValidGeometry = currentGeometries.some(
      (geometry: TaskGeometry) => !geometry.isPlaceholder
    );
    return step1Valid && (isSidebar || hasValidGeometry);
  }, [sectionValidity, currentGeometries, isSidebar]);

  const collapsedActions = useMemo(
    () => ({
      onCancel: handleClose,
      onSave: handleSubmit,
      onExpand: () => setMobilePanelMinimized(false),
      canSave,
      isSubmitting,
      saveLabel: editingTask ? t('common.updateTask') : t('common.createTask'),
    }),
    [
      handleClose,
      handleSubmit,
      setMobilePanelMinimized,
      canSave,
      isSubmitting,
      editingTask,
      t,
    ]
  );

  const headerContent = useMemo(
    () => (
      <FormPanelTitle>
        {editingTask ? t('common.editTask') : t('tasks.createNewTask')}
      </FormPanelTitle>
    ),
    [editingTask, t]
  );

  const footerContent = useMemo(
    () => (
      <FormFooter
        leftActions={
          <Button
            variant="subtle"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
        }
        rightActions={
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSave}
          >
            {editingTask ? t('common.updateTask') : t('common.createTask')}
          </Button>
        }
      />
    ),
    [handleClose, isSubmitting, t, handleSubmit, canSave, editingTask]
  );

  return (
    <SlidePanel
      opened={isOpen}
      onClose={handleClose}
      variant={slidePanelVariant}
      headerTitle={headerContent}
      footerContent={footerContent}
      width={480}
      withCloseButton={true}
      closeButtonAriaLabel={t('taskForm.closeTaskWizard')}
      isHidden={isGeometriesListOpen || isRelatedTasksListOpen}
      collapsedActions={collapsedActions}
      fullscreenWideContent={isSidebar}
      defaultFullscreen={false}
    >
      <DetailPanelLayout
        sectionItems={sectionItems}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        contentVariant="wide"
        contentClassName={
          isSidebar ? styles.wizardContentSidebar : styles.wizardContentTabs
        }
      >
        <WizardStepRenderer
          steps={stepDescriptors}
          currentStep={form.values.currentStep}
          renderProps={{
            form,
            gpsLocationSuggestion,
            isSubmitting,
            existingAttachments: editingTask?.attachments || [],
            onDeleteExistingAttachment: editingTask
              ? handleDeleteExistingAttachment
              : undefined,
            editingTaskId: editingTask?.id || null,
            handleManualRoadInput,
            handleRequestGPSDetection,
            roadSource,
            isWaitingForInitialGPS,
            members,
            isMembersLoading,
          }}
        />
      </DetailPanelLayout>
    </SlidePanel>
  );
}
